import { useEffect, useRef } from 'react';
import { getBrand } from '../../config/brand.config';
import { getAlternateBrandNames, getSiteName, getSiteUrl } from '../../config/seo.config';
import { publicApi, SiteContactLinkData } from '../../api/public.api';

const GLOBAL_SCHEMA_ID = 'tropayment-global-schema';

/**
 * Site-wide JSON-LD (@graph: Organization, PaymentService, WebAPI, WebSite).
 * Injected once on app boot — page components only add WebPage / Article extras.
 */
export default function GlobalSiteSchema() {
  const injected = useRef(false);

  useEffect(() => {
    if (injected.current) return;
    injected.current = true;

    const inject = (socialUrls: string[]) => {
      const existing = document.getElementById(GLOBAL_SCHEMA_ID);
      if (existing) existing.remove();
      document.getElementById('tropayment-boot-schema')?.remove();

      const brand = getBrand();
      const siteUrl = getSiteUrl();
      const siteName = getSiteName();
      const alternates = getAlternateBrandNames();

      const sameAs = [...new Set([siteUrl, ...socialUrls.filter((u) => u.startsWith('http'))])];

      const graph = {
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'Organization',
            '@id': `${siteUrl}/#organization`,
            name: siteName,
            alternateName: alternates,
            url: siteUrl,
            logo: { '@type': 'ImageObject', url: `${siteUrl}${brand.logoSrc}` },
            description:
              'Cryptocurrency payment gateway by tropayment. Accept USDT, USDC, and native coins on TRON, Ethereum, BSC, Polygon, Solana.',
            sameAs,
          },
          {
            '@type': 'PaymentService',
            '@id': `${siteUrl}/#payment-service`,
            name: `${siteName} Crypto Payment Gateway`,
            url: siteUrl,
            provider: { '@id': `${siteUrl}/#organization` },
            areaServed: 'Worldwide',
          },
          {
            '@type': 'WebAPI',
            '@id': `${brand.apiUrl}/#api`,
            name: `${siteName} Merchant Payment API`,
            url: brand.apiUrl,
            provider: { '@id': `${siteUrl}/#organization` },
          },
          {
            '@type': 'WebSite',
            '@id': `${siteUrl}/#website`,
            url: siteUrl,
            name: siteName,
            publisher: { '@id': `${siteUrl}/#organization` },
            inLanguage: ['en', 'ar', 'zh', 'ko'],
          },
        ],
      };

      const script = document.createElement('script');
      script.id = GLOBAL_SCHEMA_ID;
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(graph);
      document.head.appendChild(script);
    };

    publicApi
      .getSiteContacts()
      .then((contacts: SiteContactLinkData[]) => {
        const social = contacts
          .filter((c) => c.category === 'social' && c.href)
          .map((c) => c.href);
        inject(social);
      })
      .catch(() => inject([]));
  }, []);

  return null;
}
